import { useEffect, useRef, useState } from 'react';
import { TILE_EDGE_GAP, carryTile, clampTile, cornerPoint, firstTilePosition, floatingTileBounds, nearestCorner, toolbarLift, turnTile } from '../floatingTile';
import { BAR_SETTLE_MS, CALL_BAR_SELECTOR } from './web/useNameLifts.web';
/**
 * Your floating self view on the web call stage (VRSLayout.tsx), with the
 * rules of the native call stage (floatingTile.ts, PR #134). Robert,
 * 2026-10-06:
 *
 * - It starts in the bottom-right corner, TILE_EDGE_GAP (8 px) from the
 *   edges of the stage.
 * - While the call bar shows, a tile that the bar would cover moves up just
 *   clear of it (toolbarLift), and goes back down when the bar hides.
 * - It moves anywhere on the stage and stays where it is dropped: no snap.
 * - When the viewport turns (portrait and landscape), it goes to the corner
 *   it was nearest to (turnTile), never to the middle. Other changes to the
 *   stage (the window, the chat rail) keep its place as a share of the free
 *   space (carryTile).
 * - It is solid. Robert, 2026-10-08: the see-through tile (60% solid at
 *   rest) looked blurry, so he chose a solid tile on the web and in the apps.
 *
 * Pointer coordinates are relative to the video stage, never the chat rail.
 */
/** The web stage has no safe area: the tile keeps TILE_EDGE_GAP from each edge. */
const NO_INSETS = {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0
};
/** A press on the move button becomes a drag after this distance (native: 6 points). */
const DRAG_START_PX = 6;
/** The tile moves above the call bar in the time the bar slides in (css/_toolbars.scss, 0.3 s). */
const TILE_LIFT_MS = 300;
const CORNER_NAMES = {
    'bottom-left': 'bottom left',
    'bottom-right': 'bottom right',
    'top-left': 'top left',
    'top-right': 'top right'
};
/**
 * Whether two bounds are the same.
 *
 * @param {ITileBounds} left - One.
 * @param {ITileBounds} right - The other.
 * @returns {boolean}
 */
function sameBounds(left, right) {
    return left.minX === right.minX && left.maxX === right.maxX
        && left.minY === right.minY && left.maxY === right.maxY;
}
/**
 * Whether two measurements are the same.
 *
 * @param {IStageLayout|undefined} left - The earlier one.
 * @param {IStageLayout} right - The new one.
 * @returns {boolean}
 */
function sameLayout(left, right) {
    if (!left) {
        return false;
    }
    return sameBounds(left.bounds, right.bounds)
        && left.tileHeight === right.tileHeight && left.tileWidth === right.tileWidth
        && left.bar?.left === right.bar?.left && left.bar?.right === right.bar?.right
        && left.bar?.top === right.bar?.top;
}
/**
 * The corner that an arrow key moves the tile to.
 *
 * @param {TileCorner} corner - The corner the tile is nearest to.
 * @param {string} key - The arrow key.
 * @returns {TileCorner}
 */
function cornerAfterKey(corner, key) {
    const [vertical, horizontal] = corner.split('-');
    const nextVertical = key === 'ArrowUp' ? 'top' : key === 'ArrowDown' ? 'bottom' : vertical;
    const nextHorizontal = key === 'ArrowLeft' ? 'left' : key === 'ArrowRight' ? 'right' : horizontal;
    return `${nextVertical}-${nextHorizontal}`;
}
/**
 * The place and the handlers of the floating self view.
 *
 * @param {IOptions} options - Whether the tile floats and whether the call
 * bar shows.
 * @returns {Object}
 */
export default function useSelfViewPosition({ floating, toolboxVisible }) {
    const stage = useRef(null);
    const tile = useRef(null);
    const [layout, setLayout] = useState(undefined);
    // Where the tile rests (its top-left corner on the stage), and where it
    // is while it is dragged.
    const [rest, setRest] = useState(undefined);
    const [dragPoint, setDragPoint] = useState(undefined);
    const drag = useRef(null);
    const applied = useRef(null);
    /**
     * The stage, the tile and where the tile can go, as the page shows them now.
     *
     * @returns {Object|undefined}
     */
    const read = () => {
        const stageBox = stage.current?.getBoundingClientRect();
        const tileBox = tile.current?.getBoundingClientRect();
        if (!stageBox || !tileBox || !(stageBox.width > 0) || !(stageBox.height > 0)) {
            return undefined;
        }
        const tileSize = {
            height: tileBox.height,
            width: tileBox.width
        };
        return {
            bounds: floatingTileBounds({
                height: stageBox.height,
                width: stageBox.width
            }, NO_INSETS, tileSize),
            stageBox,
            tileSize
        };
    };
    useEffect(() => {
        if (!floating || typeof window === 'undefined') {
            return undefined;
        }
        const measure = () => {
            const now = read();
            if (!now) {
                return;
            }
            const landscape = window.innerWidth > window.innerHeight;
            const before = applied.current;
            applied.current = {
                bounds: now.bounds,
                landscape
            };
            if (!before || !sameBounds(before.bounds, now.bounds) || before.landscape !== landscape) {
                setRest(current => {
                    if (!current) {
                        return firstTilePosition(now.bounds);
                    }
                    if (!before) {
                        return clampTile(current, now.bounds);
                    }
                    return before.landscape === landscape
                        ? carryTile(current, before.bounds, now.bounds)
                        : turnTile(current, before.bounds, now.bounds);
                });
            }
            const barBox = toolboxVisible
                ? document.querySelector(CALL_BAR_SELECTOR)?.getBoundingClientRect() : undefined;
            const next = {
                bar: barBox && barBox.bottom > barBox.top ? {
                    left: barBox.left - now.stageBox.left,
                    right: barBox.right - now.stageBox.left,
                    top: barBox.top - now.stageBox.top
                } : undefined,
                bounds: now.bounds,
                tileHeight: now.tileSize.height,
                tileWidth: now.tileSize.width
            };
            setLayout(current => {
                if (sameLayout(current, next)) {
                    return current;
                }
                return next;
            });
        };
        measure();
        // The bar slides in: measure it again where it stops.
        const settle = window.setTimeout(measure, BAR_SETTLE_MS);
        // The chat rail and the camera-off card change the stage and the
        // tile without a window resize.
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
        stage.current && observer?.observe(stage.current);
        tile.current && observer?.observe(tile.current);
        window.addEventListener('resize', measure);
        return () => {
            window.clearTimeout(settle);
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [floating, toolboxVisible]);
    /**
     * The place under the pointer during a drag, inside the bounds.
     *
     * @param {PointerEvent} event - The pointer event.
     * @returns {ITilePoint|undefined}
     */
    const pointFor = (event) => {
        const now = read();
        const current = drag.current;
        if (!now || !current) {
            return undefined;
        }
        return clampTile({
            x: event.clientX - now.stageBox.left - current.offset.x,
            y: event.clientY - now.stageBox.top - current.offset.y
        }, now.bounds);
    };
    const cancel = () => {
        if (!drag.current) {
            return;
        }
        drag.current = null;
        setDragPoint(undefined);
    };
    const onKeyDown = (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const now = read();
        if (!now) {
            return;
        }
        const from = nearestCorner(rest ? clampTile(rest, now.bounds) : firstTilePosition(now.bounds), now.bounds);
        setRest(cornerPoint(cornerAfterKey(from, event.key), now.bounds));
    };
    // While the bar shows, the tile rises just clear of it if the bar would
    // cover it. During a drag the tile stays under the pointer.
    const bar = toolboxVisible ? layout?.bar : undefined;
    const lift = !dragPoint && rest && layout && bar
        && rest.x < bar.right && rest.x + layout.tileWidth > bar.left
        ? toolbarLift(rest, layout.tileHeight, bar.top, layout.bounds.minY) : 0;
    const point = dragPoint || rest;
    const reducedMotion = typeof window !== 'undefined'
        && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    const transition = reducedMotion ? 'none' : `transform ${TILE_LIFT_MS}ms ease`;
    // Before the first measurement, the same bottom-right corner in CSS.
    const style = point
        ? {
            left: point.x,
            top: point.y,
            transform: lift > 0 ? `translateY(-${lift}px)` : undefined,
            transition
        }
        : {
            bottom: TILE_EDGE_GAP,
            right: TILE_EDGE_GAP,
            transition
        };
    const corner = rest && layout ? nearestCorner(rest, layout.bounds) : 'bottom-right';
    return {
        handle: {
            'aria-label': `Move self view, ${CORNER_NAMES[corner]}`,
            onKeyDown,
            onLostPointerCapture: cancel,
            onPointerCancel: cancel,
            onPointerDown: (event) => {
                const box = tile.current?.getBoundingClientRect();
                if (!box || !event.isPrimary || event.button !== 0) {
                    return;
                }
                event.preventDefault();
                drag.current = {
                    id: event.pointerId,
                    moved: false,
                    offset: {
                        x: event.clientX - box.left,
                        y: event.clientY - box.top
                    },
                    start: {
                        x: event.clientX,
                        y: event.clientY
                    }
                };
                event.currentTarget.setPointerCapture(event.pointerId);
            },
            onPointerMove: (event) => {
                const current = drag.current;
                if (current?.id !== event.pointerId) {
                    return;
                }
                if (!current.moved
                    && Math.abs(event.clientX - current.start.x) + Math.abs(event.clientY - current.start.y)
                        <= DRAG_START_PX) {
                    return;
                }
                current.moved = true;
                const next = pointFor(event);
                next && setDragPoint(next);
            },
            // A drop leaves the tile where it is: no snap. A press with no
            // drag leaves it where it was.
            onPointerUp: (event) => {
                const current = drag.current;
                if (current?.id !== event.pointerId) {
                    return;
                }
                const dropped = current.moved ? pointFor(event) : undefined;
                drag.current = null;
                setDragPoint(undefined);
                dropped && setRest(dropped);
            }
        },
        stage,
        style,
        tile
    };
}
